<script lang="ts">
  import type { BufferingPolicy, ControlPolicy, RoomSettings } from '../generated';
  import type { RoomState } from '../room/state.svelte';

  const { room }: { room: RoomState } = $props();

  /** Bounds mirror the server's accepted range; the server re-validates. */
  const BARRIER_MIN_SECS = 5;
  const BARRIER_MAX_SECS = 300;
  const IDLE_MIN_MINUTES = 1;
  const IDLE_MAX_MINUTES = 1440;

  let draft = $state<RoomSettings | null>(null);
  /** Set while the form has unsent edits, so snapshots do not overwrite typing. */
  let dirty = $state(false);

  const saving = $derived(room.isBusy('change-settings'));

  $effect(() => {
    const settings = room.settings;
    if (settings === null || dirty) return;
    draft = { ...settings };
  });

  function edit<K extends keyof RoomSettings>(key: K, value: RoomSettings[K]): void {
    if (draft === null) return;
    draft = { ...draft, [key]: value };
    dirty = true;
  }

  async function save(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (draft === null) return;
    const settings: RoomSettings = {
      ...draft,
      buffering_barrier_timeout_secs: Math.min(
        BARRIER_MAX_SECS,
        Math.max(BARRIER_MIN_SECS, Math.round(draft.buffering_barrier_timeout_secs)),
      ),
      idle_reset_minutes: Math.min(
        IDLE_MAX_MINUTES,
        Math.max(IDLE_MIN_MINUTES, Math.round(draft.idle_reset_minutes)),
      ),
    };
    const outcome = await room.run({ type: 'change-settings', settings });
    if (outcome.result === 'ok') dirty = false;
  }

  function reset(): void {
    const settings = room.settings;
    if (settings === null) return;
    draft = { ...settings };
    dirty = false;
  }
</script>

{#if draft !== null}
  <section aria-labelledby="settings-heading">
    <h2 id="settings-heading">Room settings</h2>
    <p class="hint">Only you (the host) can change these. They apply to everyone immediately.</p>
    <form onsubmit={save}>
      <div class="field">
        <label for="playback-policy">Who can play, pause, seek and skip</label>
        <select
          id="playback-policy"
          value={draft.playback_policy}
          onchange={(event) => edit('playback_policy', event.currentTarget.value as ControlPolicy)}
        >
          <option value="everyone">Everyone</option>
          <option value="controllers">Host and controllers</option>
        </select>
      </div>

      <div class="field">
        <label for="queue-policy">Who can reorder, remove, clear and retry</label>
        <select
          id="queue-policy"
          value={draft.queue_policy}
          onchange={(event) => edit('queue_policy', event.currentTarget.value as ControlPolicy)}
        >
          <option value="everyone">Everyone</option>
          <option value="controllers">Host and controllers</option>
        </select>
      </div>

      <div class="field">
        <label for="buffering-policy">When someone stalls</label>
        <select
          id="buffering-policy"
          value={draft.buffering_policy}
          onchange={(event) =>
            edit('buffering_policy', event.currentTarget.value as BufferingPolicy)}
        >
          <option value="continue-and-catch-up">Keep playing, they catch up</option>
          <option value="pause-for-buffering">Pause for everyone until they recover</option>
        </select>
      </div>

      <div class="field">
        <label for="barrier-timeout">Buffering wait before staying paused (seconds)</label>
        <input
          id="barrier-timeout"
          type="number"
          min={BARRIER_MIN_SECS}
          max={BARRIER_MAX_SECS}
          step="1"
          value={draft.buffering_barrier_timeout_secs}
          oninput={(event) =>
            edit('buffering_barrier_timeout_secs', Number(event.currentTarget.value))}
        />
      </div>

      <div class="field">
        <label for="idle-reset">Reset the room after this long with nobody here (minutes)</label>
        <input
          id="idle-reset"
          type="number"
          min={IDLE_MIN_MINUTES}
          max={IDLE_MAX_MINUTES}
          step="1"
          value={draft.idle_reset_minutes}
          oninput={(event) => edit('idle_reset_minutes', Number(event.currentTarget.value))}
        />
      </div>

      <div class="actions">
        <button type="submit" class="primary" disabled={!dirty || saving || !room.live}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        <button type="button" onclick={reset} disabled={!dirty}>Discard changes</button>
      </div>
    </form>
  </section>
{/if}

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    margin-top: 0.75rem;
  }

  .field {
    display: flex;
    flex-direction: column;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .hint {
    margin: 0;
  }
</style>
